import { useMemo } from "react";

import _ from "lodash";
import { useQuery } from "react-query";

import {
  useGetLumenResultForDeal,
  useApplicantsIds,
} from "manager/hooks/api";
import { getDeal } from "manager/lib/dealsApi";
import { BACKGROUND_REPORT_TYPE, HTTP_ERROR_CODES } from "shared/config/constants";
import { usePermissions } from "shared/hooks";
import api from "shared/lib/api";
import { ApiError } from "shared/lib/errors";
import { getDealApplicationsSummary } from "shared/lib/api/dealApi";
import {
  getCachedActiveCompany,
  getCachedActiveCompanySlug,
} from "shared/utils/auth";
import { setPrefix } from "shared/utils/vrn";

import {
  mapAppPermissions,
  mapBalances,
  mapCountyCriminal,
  mapCredit,
  mapCriminal,
  mapDependents,
  mapEmployment,
  mapEvictions,
  mapFees,
  mapIdentities,
  mapIncomes,
  mapPermissions,
  mapProperty,
  mapSentilink,
  mapUnit,
  mapVoR,
} from "./mappers";
import { ApplicantType } from "./mappers/dependants/interfaces";
import { ArgyleStatus, Deal } from "./mappers/interfaces";
import {
  firstUnfinishedStep,
  getSkippedIncomeAssetsReason,
  showConditionalProceed,
  toDate,
} from "./mappers/utils";
import {
  hasExpiredBackgroundReports,
  hasSummaryApplications,
  isDealModeSummaryPayloadCompatible,
  selectRawApplications,
  shouldBlockOnFallbackLoading,
} from "./utils";

interface IGetApplicationSummary {
  dealId: number | string;
  companySlug?: string;
  enabled?: boolean;
}

interface IGetApplicationSummaryReturn {
  deal: Deal | undefined;
  rawFullApplications: any[];
  isLoading: boolean;
  dealNotFound: boolean;
  dealFetchFailed: boolean;
  dealRefetch: () => void;
}

const APPLICANT_TYPES = [
  ApplicantType.applicant,
  ApplicantType.guarantor,
  ApplicantType.occupant,
];

// Fallback path: fetch all lightweight full applications in parallel
const fetchFullApplications = async (applicationIds: number[]) => {
  if (!applicationIds || applicationIds.length === 0) {
    return [];
  }

  const results = await Promise.all(
    applicationIds.map((appId) =>
      api
        .getFullApplication({ applicationId: appId, mode: "deal" })
        .catch(() => null)
    )
  );

  return results.filter(Boolean);
};

export const useGetApplicationSummary = ({
  dealId,
  companySlug: companySlugProp,
  enabled = true,
}: IGetApplicationSummary): IGetApplicationSummaryReturn => {
  const resolvedCompanySlug = companySlugProp ?? getCachedActiveCompanySlug();
  const isQueryEnabled = Boolean(dealId) && Boolean(resolvedCompanySlug) && enabled;

  // 1c. Get deal-level data first — follow-up queries wait for confirmed access.
  const {
    data: dealFull,
    refetch: dealRefetch,
    isError: isDealError,
    error: dealError,
    isFetched: isDealFetched,
  } = useQuery(
    ["dealApplicationsSummaryDeal", resolvedCompanySlug, dealId],
    () => getDeal(dealId, { mode: "deal" }),
    {
      enabled: isQueryEnabled,
      retry: false,
    }
  );

  const dealStatusCode = (dealError as ApiError | undefined)?.statusCode;
  const dealLoadError = isDealFetched && isDealError;

  const dealBelongsToActiveCompany =
    Boolean(dealFull?.companySlug) &&
    dealFull.companySlug === resolvedCompanySlug;

  const dealCompanyMismatch =
    isDealFetched && Boolean(dealFull) && !dealBelongsToActiveCompany;

  const dealNotFound =
    dealCompanyMismatch ||
    (dealLoadError &&
      (dealStatusCode === HTTP_ERROR_CODES.notFound ||
        dealStatusCode === HTTP_ERROR_CODES.forbidden));

  const dealFetchFailed = dealLoadError && !dealNotFound;

  const dealAccessConfirmed =
    isDealFetched &&
    Boolean(dealFull) &&
    !dealLoadError &&
    dealBelongsToActiveCompany;

  // 1a. Single-call summary payload (primary path)
  const {
    data: summaryApplications,
    isLoading: isSummaryApplicationsLoading,
    isFetched: isSummaryApplicationsFetched,
    error: summaryApplicationsError,
    refetch: refetchSummaryApplications,
  } = useQuery(
    ["dealApplicationsSummary", resolvedCompanySlug, dealId],
    () => getDealApplicationsSummary(dealId, { mode: "deal" }),
    {
      enabled: isQueryEnabled && dealAccessConfirmed,
      retry: false,
    }
  );

  const isSummaryPayloadCompatible =
    isDealModeSummaryPayloadCompatible(summaryApplications);
  const shouldUseFallback =
    Boolean(summaryApplicationsError) ||
    (isSummaryApplicationsFetched && !isSummaryPayloadCompatible);

  // 1b. Fallback when summary endpoint fails or payload is incompatible
  const { applicantsIds, isApplicantIdsLoading, applicantsIdsRefetch } =
    useApplicantsIds(
      dealId,
      resolvedCompanySlug,
      isQueryEnabled && dealAccessConfirmed && shouldUseFallback
    );

  // 1d. Get lumen result
  const {
    lumenResult,
    refetchLumenResult,
  } = useGetLumenResultForDeal(
    dealId,
    isQueryEnabled && dealAccessConfirmed
  );

  // Extract application IDs from the response
  const applicationIds = useMemo(() => {
    return applicantsIds?.map((app: { id: number }) => app.id) ?? [];
  }, [applicantsIds]);

  // 2. Fallback fetch if summary endpoint failed/incompatible
  const {
    data: fullApplications = [],
    isLoading: isFullApplicationsLoading,
    isFetching: isFullApplicationsFetching,
    refetch: refetchFullApplications,
  } = useQuery(
    ["fullApplications", resolvedCompanySlug, dealId, applicationIds],
    () => fetchFullApplications(applicationIds),
    {
      enabled:
        isQueryEnabled &&
        dealAccessConfirmed &&
        shouldUseFallback &&
        applicationIds.length > 0,
      retry: false,
    }
  );

  const summaryHasData = hasSummaryApplications(summaryApplications);
  const isFallbackLoading =
    isApplicantIdsLoading ||
    isFullApplicationsLoading ||
    isFullApplicationsFetching;
  const shouldBlockOnFallback = shouldBlockOnFallbackLoading({
    shouldUseFallback,
    hasSummaryData: summaryHasData,
    hasFallbackData: fullApplications.length > 0,
    isFallbackLoading,
  });

  const dealDataReady = dealAccessConfirmed;

  const rawApplications = useMemo(
    () => {
      if (!dealDataReady) {
        return [];
      }
      return selectRawApplications({
        shouldUseFallback,
        summaryApplications,
        fullApplications,
      });
    },
    [dealDataReady, shouldUseFallback, fullApplications, summaryApplications]
  );

  const { companyPermissions } = usePermissions();

  const refetchDeal = () => {
    refetchSummaryApplications();
    if (shouldUseFallback) {
      applicantsIdsRefetch();
      refetchFullApplications();
    }
    dealRefetch();
    refetchLumenResult();
  };

  const unit = mapUnit(dealFull?.unit, dealFull?.unitListing);
  const property = mapProperty(dealFull?.unit) || dealFull?.property;
  const fees = dealFull ? mapFees(dealFull) : undefined;

  // Map application data to the expected format
  const applications = useMemo(() => {
    if (!dealDataReady) {
      return [];
    }
    return rawApplications
      .map((app) => {
        if (!app) return null;

        const { incomeAsset, applicantVrn, personVrn, party, location } = app;
        const appSummary = app.applicationSummary;

        if (!appSummary) return null;

        const creditSummary = app.backgroundCreditCheck?.creditScoreSummary;
        const criminalSummary = app.backgroundCreditCheck?.criminalRecords;
        const evictionRecords = app.backgroundCreditCheck?.evictionRecords;
        const employment = app.employmentHistories;

        const type =
          APPLICANT_TYPES[(app.applicantType ?? 0) - 1] ??
          ApplicantType.unknown;

        let appRequiresBackgroundCreditCheck = false;
        if ([ApplicantType.applicant, ApplicantType.occupant].includes(type)) {
          appRequiresBackgroundCreditCheck =
            dealFull?.requiresBackgroundCreditCheckApplicantOccupant;
        } else if (type === ApplicantType.guarantor) {
          appRequiresBackgroundCreditCheck =
            dealFull?.requiresBackgroundCreditCheckGuarantor;
        }

        const skippedIncomeAssetReason =
          getSkippedIncomeAssetsReason(appSummary);

        return {
          id: String(appSummary.id),
          status: appSummary.status,
          isPrimary: app.isPrimaryApplication,
          hideCriminalHistory: app.hideCriminalHistory,
          step: firstUnfinishedStep(
            appSummary.steps,
            app.startedAt,
            appSummary.areIncomeAndAssetChecksEnabled,
            app.skippedBgcc,
            appSummary.isPersonaEnabled,
            appSummary.isArgyleEnabled,
            appSummary.hasApplicationFee
          ),
          inviteLink: appSummary?.applicationLink,
          permission: mapAppPermissions(appSummary),
          lumenEvaluates: app.lumenEvaluates,
          verifiedIncome: app.verifiedIncome,
          totalIncome: incomeAsset?.totalIncome,
          argyleStatus: ArgyleStatus[appSummary.argyleStatus] || undefined,
          applicant: {
            id: appSummary.applicant as unknown as string,
            firstName: appSummary.firstName,
            middleName: appSummary.middleName,
            lastName: appSummary.lastName,
            email: appSummary.email,
            phone: appSummary.phoneNumber,
            birthDate: toDate(appSummary.birthdate),
            type,
            isStudent: appSummary.isStudent,
            isForeigner: appSummary.isUsCitizen === false,
            isSelfEmployed: false,
            income: mapIncomes(
              incomeAsset,
              appSummary.income,
              skippedIncomeAssetReason
            ),
            balances: mapBalances(
              incomeAsset,
              skippedIncomeAssetReason,
              app.skippedAsset
            ),
            credit: mapCredit(creditSummary, app.skippedBgcc),
            criminal: mapCriminal(criminalSummary),
            employment: mapEmployment(
              appSummary.employmentStatus,
              employment,
              appSummary.hasAutoLinkedEmployment,
              appSummary.isArgyleEnabled,
              app.incomeAsset
            ),
            eviction: mapEvictions(
              evictionRecords,
              app.skippedBgcc,
              appRequiresBackgroundCreditCheck && !dealFull?.isInNoEvictionState
            ),
            identity: mapIdentities(
              app.verifications,
              appSummary.isPersonaEnabled,
              app.idVerifications
            ),
            verificationOfResidency: mapVoR(
              app.verifications,
              dealFull?.company?.enableVerificationOfResidency
            ),
          },
          fraudIndicators:
            app.backgroundCreditCheck?.creditScoreSummary?.fraudIndicators,
          sentilink: mapSentilink(app),
          isSubmitted: appSummary?.isSubmitted ?? !!app.submittedAt,
          wasSubmitted: !!(
            app.submittedAt && appSummary?.isSubmitted === false
          ),
          submittedAt: app.submittedAt,
          startedAt: app.startedAt,
          isBlocked: appSummary.blockDealSubmitIfIncomeDataIsMissing,
          hasConditionallyProceedEnabled:
            dealFull?.hasConditionallyProceedEnabled,
          isConditionallyProceeded: dealFull?.isConditionallyProceeded,
          isCriminalComplete:
            !appRequiresBackgroundCreditCheck ||
            !!app?.backgroundCreditCheckCompleted,
          backgroundCreditCheckCompleted: !!app?.backgroundCreditCheckCompleted,
          hasUnavailableBackgroundReports: _.some(
            [creditSummary, criminalSummary, evictionRecords],
            (report) =>
              _.includes(
                [
                  BACKGROUND_REPORT_TYPE.ERROR,
                  BACKGROUND_REPORT_TYPE.UNAVAILABLE,
                ],
                _.get(report, "reportStatus")
              )
          ),
          hasExpiredBackgroundReports: hasExpiredBackgroundReports(
            app.backgroundCreditCheck
          ),
          payments: [],
          snapshot: app?.snapshot,
          // Full list: `applicant.employment` is collapsed to the most recent
          // record by mapEmployment, but the Income card lists every employer.
          employmentHistories: employment,
          employmentStatus: appSummary.employmentStatus,
          skippedIncome: appSummary.skippedIncome,
          applicantVrn: setPrefix(applicantVrn, "applicant"),
          personVrn,
          party,
          location,
          personaStatus: appSummary.personaStatus,
        };
      })
      .filter(Boolean);
  }, [dealDataReady, rawApplications, dealFull]);

  const { lumenRevision, applicantsSummary } = dealFull ?? {};

  const applicantsSummaryShort = applicantsSummary?.map(
    ({ id, skippedBgcc }) => ({
      id,
      skippedBgcc,
    })
  );

  const deal = !dealAccessConfirmed
    ? undefined
    : ({
    id: dealFull.id,
    companySlug: dealFull.companySlug,
    companyName: getCachedActiveCompany()?.name || undefined,
    status: dealFull?.dealStatus,
    applications,
    dependents: dealFull ? mapDependents(dealFull) : [],
    yardi: { pCode: dealFull?.yardiPcode, tCode: dealFull?.yardiTcode },
    agent: dealFull
      ? {
          id: dealFull?.employee?.id,
          firstName: dealFull?.employee?.user?.firstName,
          lastName: dealFull?.employee?.user?.lastName,
        }
      : undefined,
    unit,
    unitListing: dealFull?.unitListing,
    property,
    fees,
    lumenEnabled: dealFull?.lumenEnabled,
    vero1Active: dealFull?.vero1Active,
    vero1BannerState: dealFull?.vero1BannerState ?? null,
    vero1ChangedItems: dealFull?.vero1ChangedItems ?? null,
    moveInDate:
      dealFull?.appSummary?.lroMoveInDate ??
      dealFull?.applicantsSummary?.preferredLeaseStartDate,
    lumen: lumenResult
      ? {
          score: lumenResult.score,
          messages: lumenResult?.messages,
        }
      : undefined,
    permission: dealFull
      ? mapPermissions(dealFull, companyPermissions)
      : undefined,
    countyCriminal: dealFull ? mapCountyCriminal(dealFull) : undefined,
    conditionalProceed: dealFull ? showConditionalProceed(dealFull) : undefined,
    isApproved: !!dealFull?.approvedAt,
    approvedAt: dealFull?.approvedAt,
    dealUpdatedOn: dealFull?.dealUpdatedOn,
    isMagicDeal: dealFull?.isMagicDeal,
    isOpenApiDeal: dealFull?.isOpenApiDeal,
    isArchived: dealFull?.isArchived,
    lumenRevision,
    applicantsSummaryShort,
    canManuallySendToPms: dealFull?.canManuallySendToPms,
    lumenResult,
    fraudIndicators: dealFull.fraudIndicators,
    requiresSentilinkIdentification: Boolean(
      dealFull?.requiresSentilinkIdentification ||
        dealFull?.requiresApplicantOccupantSentilinkIdentification ||
        dealFull?.requiresGuarantorSentilinkIdentification
    ),
  } as unknown as Deal);

  const result: IGetApplicationSummaryReturn = {
    deal,
    // Also return raw full applications for components that need the original API structure
    rawFullApplications: rawApplications,
    isLoading:
      (isQueryEnabled && !isDealFetched) ||
      (dealAccessConfirmed &&
        !summaryHasData &&
        fullApplications.length === 0 &&
        (isSummaryApplicationsLoading || shouldBlockOnFallback)),
    dealNotFound,
    dealFetchFailed,
    dealRefetch: refetchDeal,
  };

  return result;
};
